// @flow
import { validate } from 'devx-js-utilities'
import Immutable from 'seamless-immutable'
import { selectedTariffsSerializer } from './Serializers/SelectedTariffsSerializer'
import moment from '../Core/Moment'

// config
import { config } from '../Config/ConfigLoader'

import type {
  ValidationErrorArgsValidatorType,
  ValidationErrorType,
  ValidationErrorsValidatorType,
  FieldValidationError,
  ValidationErrorsType,
  ParsedValidationErrorType,
  ArrayFieldValidationError,
} from './Types/ValidationError'
import type FieldValidator from './FieldValidator'
import type { FormValueType } from './Types/Values'
import type { UserDataType } from './Types/Values'
import type { AsMutable } from './Types/ImmutableTypes'
import type { QuoteRequestsType } from './Redux/Types/QuoteStateType'
import type QuoteInstance from './Controllers/QuoteInstance'
import { parseRelativeDatetime } from './DateTimeFunctions'
import type { FieldDTO, SpecificationDTO } from './Types/Field'
import type { ImmutableType } from './Types/ImmutableTypes'

export const createSimplyValidationError = (type: string) => ({
  error_type: type,
})

export const getMessageFromValidationError = (validationError: FieldValidationError, prefix: string = '') => {
  if (validationError && typeof validationError !== 'string') {
    let failing = validationError.failing ? `.${validationError.failing[0]}` : ''
    let errorTypeToMessage = validationError.error_type
    if (validationError.error_type !== 'string_pattern_error') {
      failing = typeof validationError.expected === 'string' ? `.${validationError.expected}` : failing
    }
    if (validationError.error_type === 'error_triggered_by_effect') {
      errorTypeToMessage = validationError.description
    }
    const prefixArray = prefix.split('.').filter((p) => !validate.isNumeric(p))
    const expectedObject =
      typeof validationError.expected === 'string' ? { expected: validationError.expected } : validationError.expected
    const data = {}
    if (typeof expectedObject === 'object') {
      if (typeof expectedObject.minRelative === 'string') {
        data.minDate = parseRelativeDatetime(expectedObject.minRelative).format('DD.MM.YYYY')
      }
      if (typeof expectedObject.maxRelative === 'string') {
        data.maxDate = parseRelativeDatetime(expectedObject.maxRelative).format('DD.MM.YYYY')
      }
    }
    return {
      msg: `${prefixArray.join('.')}${errorTypeToMessage}${failing}`,
      data: {
        ...expectedObject,
        ...data,
        now: moment().format('DD.MM.YYYY'),
      },
    }
  }
  return false
}

export const parseOneValidationError = (
  field: string,
  args: ValidationErrorArgsValidatorType,
  userData: UserDataType,
  validationErrors?: ValidationErrorType & AsMutable<any>,
  moveArrayIndexByOne?: boolean,
) => {
  const errorObject: ParsedValidationErrorType = { field, args: null }
  if (Array.isArray(args)) {
    let errors: ValidationErrorsType = {}
    if (validationErrors && validationErrors[`${field}__array`]) {
      errors = validationErrors[`${field}__array`].asMutable()
    }
    args.forEach((error: ArrayFieldValidationError) => {
      if (error.error_type === 'array_item_error') {
        let arrayIndex = error.item_index
        if (moveArrayIndexByOne) {
          arrayIndex += 1
        }
        errors[`${arrayIndex}`] = error.error
      } else {
        // $FlowFixMe
        errors = error
      }
    })
    errorObject.field = `${field}__array`
    errorObject.args = errors
  } else if (
    ['missing_string_enum_resource', 'string_enum_resource_for_dependee_undefined'].includes(args.error_type)
  ) {
    errorObject.args = {}
  } else if (userData[field] && typeof userData[field] === 'object') {
    errorObject.args = Object.keys(args).reduce((acc, argsKey) => {
      const innerErrorObject = parseOneValidationError(
        argsKey,
        args[argsKey],
        userData[field],
        undefined,
        moveArrayIndexByOne,
      )
      acc[argsKey] = innerErrorObject.args
      return acc
    }, {})
  } else {
    errorObject.args = args
  }
  return errorObject
}

export const onValidationErrorChange = (
  field: string,
  args: ValidationErrorArgsValidatorType,
  userData: UserDataType,
  cl: Function,
  validationErrors?: ValidationErrorType & AsMutable<any>,
) => {
  const error = parseOneValidationError(field, args, userData, validationErrors)
  cl(error.field, error.args)
}

export const onValidationErrorsChange = (
  errors: ValidationErrorsValidatorType,
  userData: UserDataType,
  validationErrors?: ValidationErrorType,
  moveArrayIndexByOne?: boolean,
): ValidationErrorsType =>
  errors
    ? Object.keys(errors).reduce((acc, fieldKey) => {
        const error = parseOneValidationError(
          fieldKey,
          errors[fieldKey],
          userData,
          validationErrors,
          moveArrayIndexByOne,
        )
        if (error.args) {
          acc[error.field] = error.args
        }
        return acc
      }, {})
    : {}

export const checkIfShouldBeDeleted = (
  parentKey: string,
  value: FormValueType,
  isNested: boolean,
  regexpToDelete?: RegExp,
  leaveEmptyObjects: boolean = false,
) => {
  if (isNested && value && typeof value === 'object') {
    if (regexpToDelete instanceof RegExp && !regexpToDelete.test(parentKey)) {
      return false
    }
    return Object.keys(value).reduce(
      (acc, key) =>
        typeof value[key] === 'object'
          ? (!(regexpToDelete instanceof RegExp) &&
              checkIfShouldBeDeleted('', value[key], true, regexpToDelete, leaveEmptyObjects) &&
              acc) ||
            (regexpToDelete instanceof RegExp &&
              regexpToDelete.test(key) &&
              checkIfShouldBeDeleted('', value[key], true, regexpToDelete, leaveEmptyObjects) &&
              acc)
          : acc &&
            (!value[key] ||
              value[key] === 0 ||
              (typeof value[key] === 'object' && (leaveEmptyObjects || Object.keys(value[key]).length > 0))),
      true,
    )
  }
  return false
}

export const getNewSpecification = (validator: FieldValidator, data: Object) => {
  if (validator) {
    const newValue = validator.cast(data)
    const specification = validator.callEffects(newValue)

    return [newValue, specification]
  }

  return []
}

export const onValidate = (
  productDescriptorValues: { [key: string]: any },
  key?: Array<string | number>,
  validator: FieldValidator,
  onChangeProductSpecificationCl: Function,
  userData: { [key: string]: any } = {},
) => {
  if (validator && validator.cast) {
    let [newValue, specification] = getNewSpecification(validator, { ...userData, ...productDescriptorValues })
    newValue = validator.filterDisabled({ ...userData, ...newValue }, specification)
    onChangeProductSpecificationCl(specification)
    let errors = validator.validate(newValue, specification)
    errors = Object.keys(errors).reduce((acc, errorKey) => {
      if (errors[errorKey].error_type === 'missing_string_enum_resource') {
        delete acc[errorKey]
      }
      return acc
    }, errors)
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    }
  }
  return {
    isValid: false,
    errors: {},
  }
}

export const createQuoteRequestData = (productName: string, quote?: QuoteInstance) => ({
  enabledCalculators: quote ? [quote.calculatorName] : [],
  selectedTariffs: quote ? selectedTariffsSerializer({ [productName]: quote }) : {},
  useAllTariffsIfEmpty: {
    default: productName === 'default',
    tpl: productName !== 'tpl',
    cic: productName !== 'cic',
  },
})

export const getSelectedQuote = (
  quoteRequests: QuoteRequestsType,
  selectedQuotes: { [productName: string]: string },
): QuoteInstance | {} =>
  Object.keys(quoteRequests).reduce((acc, quoteKey) => {
    if (quoteRequests[quoteKey].quotes) {
      const selected = quoteRequests[quoteKey].quotes.filter(
        (quote) => JSON.stringify(quote.subProductToTariffName) === JSON.stringify(selectedQuotes),
      )
      if (selected.length) {
        acc = selected[0]
      }
    }
    return acc
  }, {})

export const getBirthDateFromBirthNumber = (value: string = '') => {
  const val = `${value}`.trim()
  const index = parseInt(val[2], 10)
  const year = parseInt(`${val[0]}${val[1]}`, 10)
  const actualYear = moment().format('YYYY')
  const actualYearCentury = parseInt(`${actualYear[0]}${actualYear[1]}`, 10)
  const fullYear = `${
    year <= parseInt(`${actualYear[2]}${actualYear[3]}`, 10) ? actualYearCentury : actualYearCentury - 1
  }${year < 10 ? `0${year}` : year}`
  const month = `${index >= 5 ? index - 5 : index}${val[3]}`
  const date = moment(`${fullYear}-${month}-${val[4]}${val[5]}`, 'YYYY-MM-DD')
  if (date.isValid) {
    return date.format('YYYY-MM-DD')
  }
  return ''
}

export const getStepUrl = (subProductName: string | { value: string }, key: string) => {
  const name: string = subProductName.value ? subProductName.value : subProductName
  return `${config.getConfig(`steps.${name}.${key}`)}`
}

const getFieldSpecification = (specification, errorKey) => {
  if (specification && specification.fields) {
    return specification.fields.find((field) => field.name === errorKey)
  }

  if (specification && specification.domain._type === 'object' && specification.domain.specification) {
    if (specification.domain.specification.fields) {
      return specification.domain.specification.fields.find((field) => field.name === errorKey)
    }
    return specification.domain.specification
  }
  return {}
}

export const getVariableValues = (
  errors: Object,
  data: Object,
  specification: FieldDTO,
  path: string,
  defaultValues: Object | string,
) => {
  /* fix for array of errors from multiple error_triggered_by_effect errors */
  if (Array.isArray(errors) && errors.length > 1) {
    errors = errors[0]
  }

  if (errors.error_type) {
    if (
      errors.error_type === 'required_field_missing' &&
      typeof data === 'undefined' &&
      specification &&
      specification.domain &&
      specification.domain._type === 'bool'
    ) {
      data = false // bool default should be false
    } else if (
      errors.error_type === 'required_field_missing' &&
      typeof data === 'undefined' &&
      specification &&
      specification.domain &&
      specification.domain._type === 'object'
    ) {
      data = {} // object default should be {}
    } else if (
      errors.error_type === 'required_field_missing' &&
      typeof data === 'undefined' &&
      typeof defaultValues !== 'object' &&
      typeof defaultValues !== 'undefined'
    ) {
      data = defaultValues
    }
    if (typeof data === 'string' && data !== data.trim()) {
      data = data.trim()
    }
    return data
  }
  Object.keys(errors).map((errorKey) => {
    const newVal = getVariableValues(
      errors[errorKey],
      data[errorKey],
      getFieldSpecification(specification, errorKey),
      `${path}.${errorKey}`,
      defaultValues[errorKey] || {},
    )
    if (data.asMutable) {
      data = data.set(errorKey, newVal)
    } else {
      data[errorKey] = newVal
    }
  })
  return data
}

export const autofixErrors = (
  errors: Object,
  data: Object,
  specification: SpecificationDTO,
  defaultValues: Object = {},
) => {
  Object.keys(errors).forEach((errorKey) => {
    const newVal = getVariableValues(
      errors[errorKey],
      data[errorKey],
      getFieldSpecification(specification, errorKey),
      errorKey,
      defaultValues[errorKey] || {},
    )
    if (data.asMutable) {
      data = data.set(errorKey, newVal)
    } else {
      data[errorKey] = newVal
    }
  })
  return data
}

export const flattenObject = (data: Object) =>
  Object.keys(data).reduce((acc, key) => {
    if (Array.isArray(data[key]) || typeof data[key] !== 'object') {
      acc[key] = data[key]
    } else {
      const data2 = flattenObject(data[key])
      acc = {
        ...acc,
        ...Object.keys(data2).reduce((acc2, key2) => {
          acc2[`${key}.${key2}`] = data2[key2]
          return acc2
        }, {}),
      }
    }
    return acc
  }, {})

export const mergeQuoteFilters = (filters1: Object & ImmutableType, filters2: Object & ImmutableType) =>
  Object.keys(filters1).reduce((acc, key) => {
    const actualValue = filters1.getIn([key])
    if (Array.isArray(actualValue)) {
      acc = acc.setIn([key], [...actualValue, ...filters2.getIn([key], [])])
    } else if (typeof actualValue === 'object') {
      acc = acc.setIn([key], mergeQuoteFilters(actualValue, filters2.getIn([key], Immutable({}))))
    } else {
      // TODO leave actual value or replace it?
      acc = acc.setIn([key], actualValue)
    }
    return acc
  }, filters1)
